import { Box, Grid, TextField, Chip, MenuItem, InputAdornment, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../services/api';
import type { PriceRouteInfo } from '../../types';

export interface CalcParams {
  trailerId: string;
  driverId: string;
  overrideKm: string;
  overrideHours: string;
  emptyKm: string;
  emptyHours: string;
  emptyMode: 'abs' | 'pct';
  calendarDays: string;
  routeCount: string;
  marginPct: string;
}

interface ParamsPanelProps {
  params: CalcParams;
  onChange: (params: CalcParams) => void;
  routeInfo?: PriceRouteInfo | null;
}

export default function ParamsPanel({ params, onChange, routeInfo }: ParamsPanelProps) {
  const { data: trailers } = useQuery({
    queryKey: ['trailers'],
    queryFn: () => api.getTrailers(),
  });

  const { data: drivers } = useQuery({
    queryKey: ['drivers'],
    queryFn: () => api.getDrivers(),
  });

  const handleChange = (field: keyof CalcParams, value: string) => {
    onChange({ ...params, [field]: value });
  };

  const kmOverridden = params.overrideKm !== '' && routeInfo && parseFloat(params.overrideKm) !== routeInfo.googleDistanceKm;
  const hoursOverridden = params.overrideHours !== '' && routeInfo && parseFloat(params.overrideHours) !== routeInfo.googleDurationHours;

  return (
    <Box>
      <Grid container spacing={1.5}>
        <Grid item xs={6}>
          <TextField
            select
            fullWidth
            label="Køretøj"
            value={params.trailerId}
            onChange={(e) => handleChange('trailerId', e.target.value)}
          >
            <MenuItem value="">Ingen valgt</MenuItem>
            {trailers?.map((t) => (
              <MenuItem key={t.id} value={t.id}>
                {t.alias || t.number}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={6}>
          <TextField
            select
            fullWidth
            label="Chauffør"
            value={params.driverId}
            onChange={(e) => handleChange('driverId', e.target.value)}
          >
            <MenuItem value="">Ingen valgt</MenuItem>
            {drivers?.map((d) => (
              <MenuItem key={d.id} value={d.id}>
                {d.firstName} {d.lastName}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={6}>
          <TextField
            fullWidth
            label="Kilometer"
            type="number"
            value={params.overrideKm}
            onChange={(e) => handleChange('overrideKm', e.target.value)}
            InputProps={{
              endAdornment: kmOverridden && routeInfo ? (
                <Chip
                  label={`Google: ${routeInfo.googleDistanceKm} km`}
                  size="small"
                  variant="outlined"
                  color="info"
                  onClick={() => handleChange('overrideKm', String(routeInfo.googleDistanceKm))}
                  sx={{ ml: 0.5 }}
                />
              ) : undefined,
            }}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            fullWidth
            label="Timer"
            type="number"
            value={params.overrideHours}
            onChange={(e) => handleChange('overrideHours', e.target.value)}
            InputProps={{
              endAdornment: hoursOverridden && routeInfo ? (
                <Chip
                  label={`Google: ${routeInfo.googleDurationHours} t`}
                  size="small"
                  variant="outlined"
                  color="info"
                  onClick={() => handleChange('overrideHours', String(routeInfo.googleDurationHours))}
                  sx={{ ml: 0.5 }}
                />
              ) : undefined,
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <ToggleButtonGroup
              size="small"
              value={params.emptyMode}
              exclusive
              onChange={(_, v) => { if (v) handleChange('emptyMode', v); }}
              sx={{ height: 28 }}
            >
              <ToggleButton value="abs" sx={{ px: 1, fontSize: '0.75rem' }}>km / t</ToggleButton>
              <ToggleButton value="pct" sx={{ px: 1, fontSize: '0.75rem' }}>%</ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Grid>
        <Grid item xs={6}>
          <TextField
            fullWidth
            label={params.emptyMode === 'pct' ? 'Tomkørsel km %' : 'Tomkørsel km'}
            type="number"
            value={params.emptyKm}
            onChange={(e) => handleChange('emptyKm', e.target.value)}
            InputProps={{
              endAdornment: params.emptyMode === 'pct' ? (
                <InputAdornment position="end">%</InputAdornment>
              ) : undefined,
            }}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            fullWidth
            label={params.emptyMode === 'pct' ? 'Tomkørsel timer %' : 'Tomkørsel timer'}
            type="number"
            value={params.emptyHours}
            onChange={(e) => handleChange('emptyHours', e.target.value)}
            InputProps={{
              endAdornment: params.emptyMode === 'pct' ? (
                <InputAdornment position="end">%</InputAdornment>
              ) : undefined,
            }}
          />
        </Grid>

        <Grid item xs={4}>
          <TextField
            fullWidth
            label="Rute-varighed (dage)"
            type="number"
            value={params.calendarDays}
            onChange={(e) => handleChange('calendarDays', e.target.value)}
          />
        </Grid>
        <Grid item xs={4}>
          <TextField
            fullWidth
            label="Antal ture"
            type="number"
            value={params.routeCount}
            onChange={(e) => handleChange('routeCount', e.target.value)}
          />
        </Grid>
        <Grid item xs={4}>
          <TextField
            fullWidth
            label="Avance"
            type="number"
            value={params.marginPct}
            onChange={(e) => handleChange('marginPct', e.target.value)}
            InputProps={{
              endAdornment: <InputAdornment position="end">%</InputAdornment>,
            }}
          />
        </Grid>
      </Grid>
    </Box>
  );
}
